import { useState } from 'react';
import { Check, Info, Minus, Plus } from 'lucide-react';
import type { DesignStore } from '../store/design-store';
import { commands } from '../store/commands';
import type { ProductConfig, ProductVariant } from '../domain/types';
import { createConfiguratorPriceQuote, formatMoney } from '../domain/pricing';
import {
  resolveDefaultSize,
  resolveProductColour,
  resolveProductVariant,
  resolveSelectableSizes,
  resolveSizeProfile,
  resolveVariantColours,
} from '../products';
import { useDesignSnapshot } from '../store/react';
import { GarmentMockup } from './GarmentMockup';

export function OptionsStep({ product, store }: { product: ProductConfig; store: DesignStore }) {
  const [showAllColours, setShowAllColours] = useState(false);
  const { state } = useDesignSnapshot();
  const design = state.design;
  const variant = resolveProductVariant(product, design.variantId) ?? product.variants[0]!;
  const selectableSizes = resolveSelectableSizes(variant);
  const variantColours = resolveVariantColours(product, variant.id);
  const colour = resolveProductColour(product, design.colourId, variant.id);
  const visibleColours = selectVisibleColours(variantColours, colour.id, showAllColours);
  const profile = resolveSizeProfile(product, variant.id, design.size);
  const priceQuote = createConfiguratorPriceQuote(product, design);

  const setOption = (patch: Parameters<typeof commands.changeProductOption>[1]) => {
    store.dispatch(commands.changeProductOption(store.getState(), patch));
  };
  const setGarmentSize = (variantId: string, size: string) => {
    store.dispatch(commands.changeGarmentSize(store.getState(), product, variantId, size));
  };
  const setQuantity = (quantity: number) => {
    store.dispatch(commands.changeQuantity(store.getState(), quantity));
  };

  return (
    <section className="step-content split-step options-step">
      <div className="form-column">
        <div className="content-heading compact">
          <div>
            <p className="eyebrow">2 / 5 · Parametre</p>
            <h2>Nastavte produkt</h2>
          </div>
          <p>Vyberte variant, veľkosť, počet a farbu trička.</p>
        </div>

        <div className="form-section">
          <label>Variant</label>
          <div className="segmented cards">
            {product.variants.map(item => (
              <button
                key={item.id}
                type="button"
                className={item.id === variant.id ? 'active' : ''}
                onClick={() => setGarmentSize(item.id, resolveVariantSwitchSize(design.size, item))}
              >
                {item.label}
                {item.id === variant.id && <Check size={15} />}
              </button>
            ))}
          </div>
        </div>

        <div className="form-section">
          <div className="label-row">
            <label>Veľkosť</label>
            <a className="text-action" href={profile.source.url} target="_blank" rel="noreferrer">
              <Info size={14} /> Veľkostná tabuľka
            </a>
          </div>
          <div className="size-grid">
            {selectableSizes.map(size => (
              <button
                key={size}
                type="button"
                className={size === design.size ? 'active' : ''}
                onClick={() => setGarmentSize(variant.id, size)}
              >
                {size}
              </button>
            ))}
          </div>
        </div>

        <div className="form-section">
          <div className="label-row quantity-label-row">
            <label htmlFor="garment-quantity">Počet tričiek</label>
            <span className="quantity-context">Rovnaká konfigurácia</span>
          </div>
          <div className="quantity-panel">
            <div className="quantity-control">
              <button
                type="button"
                aria-label="Znížiť počet tričiek"
                disabled={design.quantity === 1}
                onClick={() => setQuantity(design.quantity - 1)}
              >
                <Minus size={16} />
              </button>
              <input
                id="garment-quantity"
                aria-label="Počet tričiek"
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                value={design.quantity}
                onChange={event => {
                  const quantity = Number(event.currentTarget.value);
                  if (Number.isSafeInteger(quantity) && quantity >= 1) setQuantity(quantity);
                }}
              />
              <button
                type="button"
                aria-label="Zvýšiť počet tričiek"
                onClick={() => setQuantity(design.quantity + 1)}
              >
                <Plus size={16} />
              </button>
            </div>
            <div className="quantity-price-preview">
              <span>Medzisúčet</span>
              <strong>
                {formatMoney(priceQuote.configuredSubtotalCents, priceQuote.currency)}
              </strong>
              <small>
                {formatMoney(priceQuote.productUnitCents, priceQuote.currency)} tričko +{' '}
                {priceQuote.transferSheetCount} ×{' '}
                {formatMoney(priceQuote.transferSheetUnitCents, priceQuote.currency)} A4 potlač / ks
              </small>
            </div>
          </div>
          <p className="field-help">
            Počet platí pre rovnakú veľkosť, farbu a dizajn. Ďalšiu veľkosť vytvorte ako samostatnú
            konfiguráciu.
          </p>
        </div>

        <div className="form-section">
          <label>Farba oblečenia</label>
          <div className="colour-grid">
            {visibleColours.map(item => (
              <button
                key={item.id}
                type="button"
                className={item.id === colour.id ? 'active' : ''}
                onClick={() => setOption({ colourId: item.id })}
              >
                <span style={{ background: item.hex }} /> <span>{item.name}</span>
                {item.id === colour.id && <Check size={15} />}
              </button>
            ))}
          </div>
          {variantColours.length > COMPACT_COLOUR_LIMIT && (
            <button
              type="button"
              className="colour-grid-toggle"
              aria-expanded={showAllColours}
              onClick={() => setShowAllColours(current => !current)}
            >
              {showAllColours ? 'Skryť' : `Zobraziť všetky farby (${variantColours.length})`}
            </button>
          )}
        </div>

        <div className="form-section">
          <label>Potlač</label>
          <div className="production-frame-fact">
            <span>
              <strong>Jeden kompletný motív na jeden hárok A4</strong>
            </span>
            <small>
              Obrázok, text a tvary na jednej strane vytvoria jeden spoločný motív. Musia sa zmestiť
              na plochu 200 × 287 mm; predná a zadná strana sa pripravujú samostatne.
            </small>
          </div>
        </div>
      </div>

      <aside className="preview-column">
        <div className="preview-sticky">
          <div className="preview-title">
            <span>Živý náhľad</span>
            <span className="status-pill">2D</span>
          </div>
          <div className="garment-preview">
            <GarmentMockup
              product={product}
              colour={colour.hex}
              variantId={variant.id}
              size={design.size}
              viewId="front"
            />
          </div>
          <div className="preview-summary">
            <SummaryRow label="Produkt" value={product.name} />
            <SummaryRow label="Variant" value={variant.label} />
            <SummaryRow label="Veľkosť" value={design.size} />
            <SummaryRow label="Farba" value={colour.name} dot={colour.hex} />
            <SummaryRow label="Počet" value={`${design.quantity} ks`} />
          </div>
        </div>
      </aside>
    </section>
  );
}

export const COMPACT_COLOUR_LIMIT = 6;

export function resolveVariantSwitchSize(
  currentSize: string,
  targetVariant: ProductVariant,
): string {
  return resolveSelectableSizes(targetVariant).includes(currentSize)
    ? currentSize
    : resolveDefaultSize(targetVariant);
}

export function selectVisibleColours<T extends { id: string }>(
  colours: readonly T[],
  selectedColourId: string,
  showAll: boolean,
): T[] {
  if (showAll || colours.length <= COMPACT_COLOUR_LIMIT) return [...colours];
  const popular = colours.slice(0, COMPACT_COLOUR_LIMIT);
  if (popular.some(item => item.id === selectedColourId)) return popular;
  const selected = colours.find(item => item.id === selectedColourId);
  return selected ? [...popular.slice(0, COMPACT_COLOUR_LIMIT - 1), selected] : popular;
}

export function SummaryRow({ label, value, dot }: { label: string; value: string; dot?: string }) {
  return (
    <div className="summary-row">
      <span>{label}</span>
      <strong>
        {dot && <i style={{ background: dot }} />}
        {value}
      </strong>
    </div>
  );
}

export function formatMm(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1).replace('.', ',');
}

export function formatRatio(part: number, whole: number): string {
  return `${Math.round((part / whole) * 100)} %`;
}
